import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  FlagOutlined as StatusIcon,
  CategoryOutlined as TypeIcon,
  LocationOnOutlined as LocationIcon,
  EventOutlined as DateIcon,
  SquareFootOutlined as AreaIcon,
  MapOutlined as MapIcon,
  PersonOutlined as PersonIcon,
  PhoneOutlined as PhoneIcon,
  EmailOutlined as EmailIcon,
  ScheduleOutlined as ScheduleIcon,
  AccessTimeOutlined as TimeIcon,
  Inventory2Outlined as CapacityIcon,
  NotesOutlined as NotesIcon,
  InfoOutlined as InfoIcon,
  Edit as EditIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import EntityDetailRow from '../common/EntityDetailRow';
import { CollectionSiteSchema, CollectionSiteType } from '../../types/collection-site.type';
import {
  displayField,
  formatSiteDateTime,
} from '../../helpers/collection-site.helpers';

function softChipSx(color: string) {
  return {
    height: 26,
    fontWeight: 600,
    bgcolor: alpha(color, 0.1),
    color,
    border: `1px solid ${alpha(color, 0.22)}`,
  } as const;
}

function ViewSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'none',
        display: 'block',
        mb: 1,
        mt: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

function DetailValue({
  children,
  muted,
  mono,
  preWrap,
  title,
}: {
  children: React.ReactNode;
  muted?: boolean;
  mono?: boolean;
  preWrap?: boolean;
  title?: string;
}) {
  return (
    <Typography
      variant="body2"
      title={title}
      sx={{
        fontWeight: muted ? 400 : 500,
        color: muted ? 'text.secondary' : 'text.primary',
        textAlign: 'end',
        fontFamily: mono ? 'monospace' : undefined,
        fontSize: mono ? '0.875rem' : undefined,
        whiteSpace: preWrap ? 'pre-wrap' : 'nowrap',
        overflow: preWrap ? undefined : 'hidden',
        textOverflow: preWrap ? undefined : 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {children}
    </Typography>
  );
}

function hasText(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

export interface CollectionSiteViewDialogProps {
  site: CollectionSiteSchema | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (site: CollectionSiteSchema) => void;
}

const CollectionSiteViewDialog: React.FC<CollectionSiteViewDialogProps> = ({
  site,
  open,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!site) return null;

  const displayName = site.data?.name || t('common.siteFallback', { id: site.id.slice(0, 8) });
  const siteType = site.data?.type as CollectionSiteType | undefined;
  const statusLabel = site.status?.trim();
  const address = site.data?.address;
  const contact = site.data?.contact_info;
  const schedule = site.data?.schedule;
  const pickupDays = Array.isArray(schedule?.pickup_days)
    ? schedule.pickup_days
        .map((day: string) => t(`collectionSites.days.${day}`, { defaultValue: day }))
        .join(', ')
    : '';

  const hasCoords = site.latitude != null && site.longitude != null;
  const coordinates = hasCoords ? `${site.latitude}, ${site.longitude}` : null;

  const addressFields = [
    { label: t('collectionSites.streetAddress'), value: address?.street },
    { label: t('common.city'), value: address?.city },
    { label: t('common.state'), value: address?.state },
    { label: t('common.country'), value: address?.country },
    { label: t('common.postalCode'), value: address?.postal_code },
  ].filter((field) => hasText(field.value));

  const hasContact = [contact?.name, contact?.phone, contact?.email].some(hasText);
  const hasSchedule = [pickupDays, schedule?.pickup_time, site.data?.operating_hours].some(hasText);
  const hasNotes = hasText(site.data?.special_instructions) || hasText(site.data?.notes);
  const hasLocationSection =
    hasCoords || hasText(site.google_maps_url) || addressFields.length > 0;

  const typeLabel = siteType
    ? t(`collectionSites.types.${siteType}`, { defaultValue: siteType })
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <PlaceIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {t('collectionSites.siteDetails')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={displayName}
              sx={{ display: 'block', mt: 0.25 }}
            >
              {displayName}
            </Typography>
            {typeLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={typeLabel}
                sx={{ display: 'block' }}
              >
                {typeLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        <Stack spacing={2} divider={<Divider flexItem sx={{ opacity: 0.55 }} />}>
          <Box>
            <ViewSectionTitle>{t('collectionSites.basicInfo')}</ViewSectionTitle>
            <Stack spacing={1.25}>
              <EntityDetailRow icon={<TypeIcon />} label={t('collectionSites.card.type')}>
                {typeLabel ? (
                  <Chip size="small" label={typeLabel} sx={softChipSx(theme.palette.info.main)} />
                ) : (
                  <DetailValue muted>—</DetailValue>
                )}
              </EntityDetailRow>

              <EntityDetailRow icon={<StatusIcon />} label={t('collectionSites.card.status')}>
                {statusLabel ? (
                  <Chip size="small" label={statusLabel} sx={softChipSx(theme.palette.text.primary)} />
                ) : (
                  <DetailValue muted>—</DetailValue>
                )}
              </EntityDetailRow>

              <EntityDetailRow icon={<AreaIcon />} label={t('collectionSites.card.area')}>
                <DetailValue muted={!hasText(site.area)}>{displayField(site.area)}</DetailValue>
              </EntityDetailRow>

              <EntityDetailRow icon={<CapacityIcon />} label={t('common.capacity')}>
                <DetailValue muted={!hasText(site.data?.capacity)}>
                  {displayField(site.data?.capacity)}
                </DetailValue>
              </EntityDetailRow>
            </Stack>
          </Box>

          {hasLocationSection && (
            <Box>
              <ViewSectionTitle>{t('collectionSites.addressInfo')}</ViewSectionTitle>
              <Stack spacing={1.25}>
                {coordinates && (
                  <EntityDetailRow icon={<LocationIcon />} label={t('collectionSites.card.location')}>
                    <DetailValue mono title={coordinates}>
                      {coordinates}
                    </DetailValue>
                  </EntityDetailRow>
                )}

                {site.google_maps_url && (
                  <EntityDetailRow icon={<MapIcon />} label={t('collectionSites.googleMapsUrl')}>
                    <Link
                      href={site.google_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ fontWeight: 500, textAlign: 'end', maxWidth: '100%', display: 'block' }}
                      noWrap
                    >
                      {t('collectionSites.openInMaps')}
                    </Link>
                  </EntityDetailRow>
                )}

                {addressFields.map((field) => (
                  <EntityDetailRow key={field.label} icon={<LocationIcon />} label={field.label}>
                    <DetailValue title={String(field.value)}>{String(field.value).trim()}</DetailValue>
                  </EntityDetailRow>
                ))}
              </Stack>
            </Box>
          )}

          {hasContact && (
            <Box>
              <ViewSectionTitle>{t('collectionSites.contactInfo')}</ViewSectionTitle>
              <Stack spacing={1.25}>
                <EntityDetailRow icon={<PersonIcon />} label={t('collectionSites.contactName')}>
                  <DetailValue muted={!hasText(contact?.name)}>{displayField(contact?.name)}</DetailValue>
                </EntityDetailRow>

                <EntityDetailRow icon={<PhoneIcon />} label={t('common.phone')}>
                  <DetailValue muted={!hasText(contact?.phone)}>{displayField(contact?.phone)}</DetailValue>
                </EntityDetailRow>

                <EntityDetailRow icon={<EmailIcon />} label={t('common.email')}>
                  <DetailValue muted={!hasText(contact?.email)}>{displayField(contact?.email)}</DetailValue>
                </EntityDetailRow>
              </Stack>
            </Box>
          )}

          {hasSchedule && (
            <Box>
              <ViewSectionTitle>{t('collectionSites.scheduleInfo')}</ViewSectionTitle>
              <Stack spacing={1.25}>
                <EntityDetailRow icon={<ScheduleIcon />} label={t('collectionSites.pickupDays')}>
                  <DetailValue muted={!pickupDays}>{pickupDays || '—'}</DetailValue>
                </EntityDetailRow>

                <EntityDetailRow icon={<TimeIcon />} label={t('collectionSites.pickupTime')}>
                  <DetailValue muted={!hasText(schedule?.pickup_time)}>
                    {displayField(schedule?.pickup_time)}
                  </DetailValue>
                </EntityDetailRow>

                <EntityDetailRow icon={<TimeIcon />} label={t('collectionSites.operatingHours')}>
                  <DetailValue muted={!hasText(site.data?.operating_hours)}>
                    {displayField(site.data?.operating_hours)}
                  </DetailValue>
                </EntityDetailRow>
              </Stack>
            </Box>
          )}

          {hasNotes && (
            <Box>
              <ViewSectionTitle>{t('common.notes')}</ViewSectionTitle>
              <Stack spacing={1.25}>
                {hasText(site.data?.special_instructions) && (
                  <EntityDetailRow icon={<InfoIcon />} label={t('collectionSites.specialInstructions')}>
                    <DetailValue preWrap>{site.data?.special_instructions?.trim()}</DetailValue>
                  </EntityDetailRow>
                )}

                {hasText(site.data?.notes) && (
                  <EntityDetailRow icon={<NotesIcon />} label={t('common.notes')}>
                    <DetailValue preWrap>{site.data?.notes?.trim()}</DetailValue>
                  </EntityDetailRow>
                )}
              </Stack>
            </Box>
          )}

          <Box>
            <ViewSectionTitle>{t('collectionSites.viewSections.timeline')}</ViewSectionTitle>
            <Stack spacing={1.25}>
              <EntityDetailRow icon={<DateIcon />} label={t('collectionSites.createdLabel')}>
                <DetailValue muted>{formatSiteDateTime(site.created_at)}</DetailValue>
              </EntityDetailRow>

              <EntityDetailRow icon={<DateIcon />} label={t('collectionSites.updatedLabel')}>
                <DetailValue muted>{formatSiteDateTime(site.updated_at)}</DetailValue>
              </EntityDetailRow>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('common.close')}
        </Button>
        {onEdit && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              onEdit(site);
              onClose();
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('common.edit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CollectionSiteViewDialog;
